import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, Loader2, Lock } from "lucide-react";
import { api, errMsg, fmtDateTime, downloadFile } from "@/lib/api";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.heic,.tif,.tiff";

export function InternalFiles({ c, onChanged }: { c: any; onChanged: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const files = (c.internal_files || []).filter((f: any) => !f.is_deleted);
  const upload = async (list: FileList | null) => {
    if (!list?.length) return;
    setBusy(true);
    for (const file of Array.from(list)) {
      const fd = new FormData();
      fd.append("file", file);
      try { await api.post(`/admin/cases/${c.id}/internal-files`, fd); toast.success(`Attached ${file.name}`); } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    if (ref.current) ref.current.value = "";
    setBusy(false);
    onChanged();
  };
  const download = async (f: any) => {
    try {
      await downloadFile(`/admin/cases/${c.id}/internal-files/${f.id}`, f.filename);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (f: any) => {
    if (!window.confirm(`Remove "${f.filename}" from this case?`)) return;
    try { await api.delete(`/admin/cases/${c.id}/internal-files/${f.id}`); toast.success("File removed"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid="internal-files">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#64748B]"><Lock className="h-3.5 w-3.5" /> Internal files: staff only, never sent or added to reports</p>
        <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={() => ref.current?.click()} data-testid="internal-file-attach-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach file
        </button>
        <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files)} data-testid="internal-file-input" />
      </div>
      {files.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="internal-files-list">
          {files.map((f: any) => (
            <li key={f.id} className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] px-3 py-2 text-sm" data-testid={`internal-file-${f.id}`}>
              <Paperclip className="h-4 w-4 shrink-0 text-[#94A3B8]" />
              <div className="min-w-0 flex-1"><p className="truncate font-semibold text-[#1A2536]">{f.filename}</p><p className="text-xs text-[#94A3B8]">{f.size < 1024 ? `${f.size} bytes` : f.size < 1048576 ? `${(f.size / 1024).toFixed(0)} KB` : `${(f.size / 1048576).toFixed(1)} MB`} · {f.by} · {fmtDateTime(f.at)}</p></div>
              <button type="button" className="btn-secondary btn-sm" title="Download" aria-label="Download" onClick={() => download(f)} data-testid={`internal-file-download-${f.id}`}><Download className="h-4 w-4" /></button>
              <button type="button" className="btn-secondary btn-sm text-[#B91C1C]" title="Remove" aria-label="Remove" onClick={() => remove(f)} data-testid={`internal-file-remove-${f.id}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
